// GAVION custom lead statuses (spec docs/superpowers/specs/2026-10-19-lead-statuses-design.md §1–§2). The one TS
// lead-status helper: the badge, the list filters, the lead sheet, the dialer / booking / data-quality twins, the
// automation editor, the email segments and Paramètres → Statuts des leads read the configured statuses through these
// functions. SQL twins (supabase/schemas/63_gavion_lead_statuses.sql): DEFAULT_LEAD_STATUSES / SYSTEM_LEAD_STATUSES =
// private.default_lead_statuses / private.system_lead_statuses (pinned by scripts/gavion/opsParity.test.ts),
// isCustomLeadStatus = private.lead_status_is_custom, validateLeadStatuses = the refusal codes of
// public.set_lead_statuses, isLeadStatusMoveTarget = public.delete_lead_status's target rule. Pure: no React.
import {
  slugFromLabel,
  STAGE_COLOR_RE,
  STAGE_LABEL_MAX,
  STAGE_VALUE_RE,
} from "../deals/stageModel";

export type LeadStatusDef = { value: string; label: string; color: string };

/** The values of the former enum public.prospect_status: renamed, recoloured, reordered, never deleted. */
export const SYSTEM_LEAD_STATUSES = [
  "new",
  "in_progress",
  "callback",
  "demo_booked",
  "in_pipeline", // GAVION call-brief-pipeline (83_)
  "not_interested",
  "bad_number",
  "do_not_call",
  "customer",
  "cooling_down",
] as const;
export type SystemLeadStatus = (typeof SYSTEM_LEAD_STATUSES)[number];
export const LEAD_STATUS_LABEL_MAX = STAGE_LABEL_MAX;
export const LEAD_STATUS_VALUE_RE = STAGE_VALUE_RE;
export const LEAD_STATUS_COLOR_RE = STAGE_COLOR_RE;
/** A status the list does not hold (a lead whose status was deleted meanwhile). */
export const UNKNOWN_LEAD_STATUS_COLOR = "#94a3b8";

export const DEFAULT_LEAD_STATUSES: readonly LeadStatusDef[] = Object.freeze([
  { value: "new", label: "Nouveau", color: "#64748b" },
  { value: "in_progress", label: "En cours", color: "#2563eb" },
  { value: "callback", label: "Rappel", color: "#d97706" },
  { value: "demo_booked", label: "Démo réservée", color: "#7c3aed" },
  { value: "in_pipeline", label: "En pipeline", color: "#0d9488" }, // GAVION call-brief-pipeline (83_)
  { value: "not_interested", label: "Pas intéressé", color: "#ea580c" },
  { value: "bad_number", label: "Mauvais numéro", color: "#db2777" },
  { value: "do_not_call", label: "Ne pas appeler", color: "#dc2626" },
  { value: "customer", label: "Client", color: "#16a34a" },
  { value: "cooling_down", label: "En pause", color: "#475569" },
]);

/** Spec §3: where the leads of a deleted added status may go, besides another added status. */
export const MOVE_TARGET_SYSTEM_STATUSES: readonly string[] = Object.freeze([
  "new",
  "in_progress",
  "callback",
  "not_interested",
]);
/** Spec §4: never offered in the lead sheet (do_not_call: « Ne pas appeler » action; cooling_down: set by the rules; in_pipeline: set by « Envoyer dans le pipeline »). */
export const NOT_PICKABLE_STATUSES: readonly string[] = Object.freeze([
  "do_not_call",
  "cooling_down",
  "in_pipeline", // GAVION call-brief-pipeline: set by « Envoyer dans le pipeline »
]);

export const isSystemLeadStatus = (value: string): boolean =>
  (SYSTEM_LEAD_STATUSES as readonly string[]).includes(value);

const findStatus = (statuses: readonly LeadStatusDef[], value: string) =>
  statuses.find((s) => s.value === value);

/** An added status: configured and not a system one. It behaves like « En cours » (private.lead_status_is_custom). */
export const isCustomLeadStatus = (
  statuses: readonly LeadStatusDef[],
  value: string | null | undefined,
): boolean =>
  typeof value === "string" &&
  !isSystemLeadStatus(value) &&
  findStatus(statuses, value) !== undefined;

/**
 * A stored config.leadStatuses (any shape) as the app reads it: the stored order; entries without a string value are
 * dropped and a repeated value keeps its first entry; label trimmed, else the default label, else the value (60 max);
 * colour a valid #rrggbb in lower case, else the default colour, else grey; a system status the list lacks is appended
 * with its defaults (the RPC never stores such a list; this keeps an older or hand-edited one usable). Nothing stored →
 * the defaults (the configuration has not loaded, or 63 is not applied yet).
 */
export const normalizeLeadStatuses = (raw: unknown): LeadStatusDef[] => {
  const src: readonly unknown[] =
    Array.isArray(raw) && raw.length > 0 ? raw : DEFAULT_LEAD_STATUSES;
  const out: LeadStatusDef[] = [];
  for (const e of src) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const r = e as Record<string, unknown>;
    if (typeof r.value !== "string" || r.value.trim() === "") continue;
    const value = r.value;
    if (findStatus(out, value)) continue;
    const d = findStatus(DEFAULT_LEAD_STATUSES, value);
    const rawLabel = typeof r.label === "string" ? r.label.trim() : "";
    const label = (rawLabel || d?.label || value).slice(
      0,
      LEAD_STATUS_LABEL_MAX,
    );
    const color =
      typeof r.color === "string" && LEAD_STATUS_COLOR_RE.test(r.color)
        ? r.color.toLowerCase()
        : (d?.color ?? UNKNOWN_LEAD_STATUS_COLOR);
    out.push({ value, label, color });
  }
  for (const d of DEFAULT_LEAD_STATUSES)
    if (!findStatus(out, d.value)) out.push({ ...d });
  return out;
};

export const leadStatusColor = (
  statuses: readonly LeadStatusDef[],
  value: string,
): string => findStatus(statuses, value)?.color ?? UNKNOWN_LEAD_STATUS_COLOR;

/** A new status's value: its label without accents, lower case, `_` between words (50 max), never a system or taken value. */
export const leadStatusSlug = (
  label: string,
  taken: readonly string[],
): string => slugFromLabel(label, taken, SYSTEM_LEAD_STATUSES, "statut");

export type LeadStatusIssueCode =
  | "invalid_lead_status_value"
  | "duplicate_lead_status"
  | "invalid_lead_status_label"
  | "invalid_lead_status_color"
  | "missing_system_lead_status";
export type LeadStatusIssue = { code: LeadStatusIssueCode; value: string };

/** The client-side twin of public.set_lead_statuses's checks (same codes, same order). */
export const validateLeadStatuses = (
  statuses: readonly LeadStatusDef[],
): LeadStatusIssue | null => {
  const seen = new Set<string>();
  for (const s of statuses) {
    if (!LEAD_STATUS_VALUE_RE.test(s.value))
      return { code: "invalid_lead_status_value", value: s.value };
    if (seen.has(s.value))
      return { code: "duplicate_lead_status", value: s.value };
    seen.add(s.value);
    const label = s.label.trim();
    if (!label || label.length > LEAD_STATUS_LABEL_MAX)
      return { code: "invalid_lead_status_label", value: s.value };
    if (!LEAD_STATUS_COLOR_RE.test(s.color))
      return { code: "invalid_lead_status_color", value: s.value };
  }
  const missing = SYSTEM_LEAD_STATUSES.filter((v) => !seen.has(v));
  return missing.length
    ? { code: "missing_system_lead_status", value: missing.join(", ") }
    : null;
};

/** A system status still carries its default (French) label: the screens show its translation. */
export const hasDefaultLeadLabel = (status: LeadStatusDef): boolean =>
  findStatus(DEFAULT_LEAD_STATUSES, status.value)?.label === status.label;

/** The label shown for a status value (spec §1): the owner's label once renamed, in both languages. */
export const leadStatusLabelFor = (
  statuses: readonly LeadStatusDef[],
  value: string,
  translate: (key: string, options: { _: string }) => string,
): string => {
  const status = findStatus(statuses, value);
  if (!status)
    return translate(`gavion.prospect_status.${value}`, { _: value });
  return hasDefaultLeadLabel(status)
    ? translate(`gavion.prospect_status.${value}`, { _: status.label })
    : status.label;
};

/** Twin of public.delete_lead_status's target rule (spec §3). */
export const isLeadStatusMoveTarget = (
  statuses: readonly LeadStatusDef[],
  deleting: string,
  target: string,
): boolean =>
  target !== deleting &&
  findStatus(statuses, target) !== undefined &&
  (MOVE_TARGET_SYSTEM_STATUSES.includes(target) || !isSystemLeadStatus(target));

/** The targets offered by the delete dialog, in the configured order. */
export const leadStatusMoveTargets = (
  statuses: readonly LeadStatusDef[],
  deleting: string,
): LeadStatusDef[] =>
  statuses.filter((s) => isLeadStatusMoveTarget(statuses, deleting, s.value));

/**
 * The configured statuses out of the `configuration` rows (row 1's config.leadStatuses). The one reader of the FakeRest /
 * local twins; a store without row 1 or without the list reads the defaults.
 */
export const leadStatusesFromConfigRows = (
  rows: ReadonlyArray<{ id: unknown; config?: unknown }>,
): LeadStatusDef[] => {
  const config = rows.find((r) => String(r.id) === "1")?.config;
  return normalizeLeadStatuses(
    config && typeof config === "object"
      ? (config as { leadStatuses?: unknown }).leadStatuses
      : undefined,
  );
};
